import {
    Grid2,
    TextField,
    Typography,
    Divider,
    Button,
    Box,
    MenuItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Autocomplete,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CasinoIcon from '@mui/icons-material/Casino';
import { useTranslation } from 'react-i18next';

const NIGHT_MODES = ['auto', 'light', 'dark'];

// Union of these two arrays must equal backend/src/models/Shows.ts
// SHOW_STYLING_KEYS (and the widget's STYLING_KEYS allowlist). The split
// here is UI-only — which keys land in Basics vs the Advanced disclosure.
const BASIC_COLOR_KEYS = [
    { key: 'primary', defaultValue: '#002de5' },
    { key: 'header-color', defaultValue: '#1e2328' },
    { key: 'default-color', defaultValue: '#1e2328' },
    { key: 'default-bg', defaultValue: '#f0f0f0' },
];

// Legacy 8 keys kept as escape hatches behind the Advanced disclosure.
// CSS color-mix() in 03_variables.styl handles these by default; admins
// only need to fill them in when they want to override the derived value.
const ADVANCED_COLOR_KEYS = [
    { key: 'primary-bg', defaultValue: '#002de5' },
    { key: 'primary-bg-active', defaultValue: '#003d99' },
    { key: 'primary-bg-hover-and-focus', defaultValue: '#3390ff' },
    { key: 'primary-border', defaultValue: '#002de5' },
    { key: 'primary-color', defaultValue: '#ffffff' },
    { key: 'secondary', defaultValue: '#3390ff' },
    { key: 'featured-card-bg', defaultValue: '#3390ff' },
    { key: 'featured-card-color', defaultValue: '#ffffff' },
];

// Short curated list as autocomplete suggestions. The field is freeSolo so
// admins can type any Google Fonts family name; backend pattern validates.
const POPULAR_GOOGLE_FONTS = [
    'Inter',
    'Roboto',
    'Open Sans',
    'Lato',
    'Poppins',
    'Montserrat',
    'Oswald',
    'Raleway',
    'Playfair Display',
    'Bebas Neue',
    'Lora',
    'Pacifico',
];

const HEX_COLOR_REGEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

function hslHex(h, s, l) {
    const a = (s / 100) * Math.min(l / 100, 1 - l / 100);
    const f = (n) => {
        const k = (n + h / 30) % 12;
        const c = l / 100 - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
        return Math.round(c * 255)
            .toString(16)
            .padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
}

// Pick a random hue, then derive the 4 basic-tab colors from it so the
// palette feels coordinated. color-mix() in 03_variables.styl handles every
// hover/active/border state from --fireknife-primary on its own.
function generatePalette() {
    const hue = Math.floor(Math.random() * 360);
    return {
        primary: hslHex(hue, 70, 50),
        'header-color': hslHex(hue, 50, 20),
        'default-color': hslHex(hue, 12, 16),
        'default-bg': hslHex(hue, 5, 97),
    };
}

function ColorRow({ keyName, defaultValue, styling, updateStyling, t }) {
    const current = styling[keyName] || '';
    return (
        <Grid2 size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                    type="color"
                    value={current || defaultValue}
                    onChange={(e) => updateStyling(keyName, e.target.value)}
                    sx={{ width: 56, flexShrink: 0, '& input': { padding: '4px', height: 36 } }}
                    inputProps={{ 'aria-label': t(`shows.stylingFields.${keyName}`) }}
                />
                <TextField
                    fullWidth
                    size="small"
                    label={t(`shows.stylingFields.${keyName}`)}
                    value={current}
                    placeholder={defaultValue}
                    onChange={(e) => updateStyling(keyName, e.target.value)}
                    error={Boolean(current) && !HEX_COLOR_REGEX.test(current)}
                    helperText={
                        current && !HEX_COLOR_REGEX.test(current)
                            ? t('shows.stylingInvalidHex')
                            : ''
                    }
                    // Always-floated label: keeps Basics (with values) and Advanced
                    // (empty by default) aligned on a single visual baseline, and
                    // lets the hex placeholder show through as a fallback hint.
                    InputLabelProps={{ shrink: true }}
                />
            </Box>
        </Grid2>
    );
}

function FontAutocomplete({ label, value, onChange }) {
    return (
        <Autocomplete
            freeSolo
            autoSelect
            size="small"
            options={POPULAR_GOOGLE_FONTS}
            value={value || ''}
            onChange={(_e, next) => onChange(next || undefined)}
            onInputChange={(_e, next, reason) => {
                if (reason === 'clear') onChange(undefined);
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder="e.g. Inter"
                    InputLabelProps={{ shrink: true }}
                />
            )}
        />
    );
}

function ShowStylingSection({ values, setFieldValue }) {
    const { t } = useTranslation();
    const marketing = values.marketing || {};
    const styling = marketing.styling || {};
    const fonts = marketing.fonts || {};
    const nightMode = marketing.nightMode || '';

    const updateMarketing = (patch) => setFieldValue('marketing', { ...marketing, ...patch });

    const updateStyling = (key, value) => {
        const next = { ...styling };
        if (value) next[key] = value.toLowerCase();
        else delete next[key];
        updateMarketing({ styling: next });
    };

    const updateFont = (slot, value) => {
        const next = { ...fonts };
        if (value) next[slot] = value;
        else delete next[slot];
        updateMarketing({ fonts: next });
    };

    return (
        <>
            <Grid2 size={{ xs: 12 }} sx={{ textAlign: 'left' }}>
                <Typography variant="h6" gutterBottom>
                    {t('shows.stylingTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('shows.stylingDescription')}
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        mt: 2,
                    }}
                >
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() =>
                            // Replace (not merge) so prior advanced overrides don't shadow
                            // the new palette; color-mix() regenerates the rest.
                            updateMarketing({ styling: generatePalette() })
                        }
                        startIcon={<CasinoIcon />}
                    >
                        {t('shows.stylingSurpriseMe')}
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() =>
                            updateMarketing({ styling: {}, fonts: {}, nightMode: undefined })
                        }
                    >
                        {t('shows.stylingResetAll')}
                    </Button>
                    <TextField
                        select
                        size="small"
                        label={t('shows.stylingNightMode')}
                        value={nightMode}
                        onChange={(e) =>
                            updateMarketing({ nightMode: e.target.value || undefined })
                        }
                        InputLabelProps={{ shrink: true }}
                        sx={{
                            ml: { xs: 0, sm: 'auto' },
                            width: { xs: '100%', sm: 180 },
                        }}
                    >
                        <MenuItem value="">—</MenuItem>
                        {NIGHT_MODES.map((m) => (
                            <MenuItem key={m} value={m}>
                                {t(`shows.stylingNightMode${m[0].toUpperCase() + m.slice(1)}`)}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
                <Divider sx={{ my: 2 }} />
            </Grid2>

            <Grid2 size={{ xs: 12 }} sx={{ textAlign: 'left' }}>
                <Typography variant="subtitle1">{t('shows.stylingBasicsTitle')}</Typography>
            </Grid2>

            {BASIC_COLOR_KEYS.map(({ key, defaultValue }) => (
                <ColorRow
                    key={key}
                    keyName={key}
                    defaultValue={defaultValue}
                    styling={styling}
                    updateStyling={updateStyling}
                    t={t}
                />
            ))}

            <Grid2 size={{ xs: 12 }} sx={{ textAlign: 'left', mt: 1 }}>
                <Typography variant="subtitle1">{t('shows.stylingFontsTitle')}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('shows.stylingFontsDescription')}
                </Typography>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
                <FontAutocomplete
                    label={t('shows.stylingFontHeader')}
                    value={fonts.header}
                    onChange={(name) => updateFont('header', name)}
                />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
                <FontAutocomplete
                    label={t('shows.stylingFontBody')}
                    value={fonts.body}
                    onChange={(name) => updateFont('body', name)}
                />
            </Grid2>

            <Grid2 size={{ xs: 12 }} sx={{ textAlign: 'left', mt: 1 }}>
                {/* Strip Accordion's default paper/gutter chrome so the advanced
                    color rows flow inline at the same left edge as Basics and
                    Fonts — the disclosure is purely a collapse toggle, not a
                    separate card. */}
                <Accordion
                    disableGutters
                    elevation={0}
                    square
                    sx={{
                        background: 'transparent',
                        '&:before': { display: 'none' },
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                            px: 0,
                            minHeight: 0,
                            '& .MuiAccordionSummary-content': { my: 1 },
                        }}
                    >
                        <Typography variant="subtitle1">
                            {t('shows.stylingAdvancedTitle')}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0, pt: 0 }}>
                        <Grid2 container spacing={2}>
                            {ADVANCED_COLOR_KEYS.map(({ key, defaultValue }) => (
                                <ColorRow
                                    key={key}
                                    keyName={key}
                                    defaultValue={defaultValue}
                                    styling={styling}
                                    updateStyling={updateStyling}
                                    t={t}
                                />
                            ))}
                        </Grid2>
                    </AccordionDetails>
                </Accordion>
            </Grid2>
        </>
    );
}

export default ShowStylingSection;
